import { useMemo, useState } from "react";
import { TextField } from "@mui/material";
import acceptanceService, {
  IAcceptanceTransferRequest,
} from "api/acceptanceService";
import workspaceService from "api/workspaceService";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import { IWorkspaceMember } from "dto/IWorkspace";
import styles from "./styles.module.scss";

/** Identyfikacja przekazywanego pliku/encji (wspólna dla wszystkich menu plików). */
export interface TransferFileRef {
  /** Tytuł/opis pozycji widoczny u odbiorcy. */
  title: string;
  fileName?: string;
  /** Uri pliku (Files) — do re‑wrap klucza obiektu dla odbiorcy po stronie BE. */
  fileUri?: string;
  encryptionKeyId?: string;
  /** Typ obiektu źródłowego: „file" | „package" | „dossier" | „drive". */
  documentType: string;
  documentId?: string;
}

interface Props {
  file: TransferFileRef;
  /** „received" → Otrzymane (akceptacja/podpis), „notifications" → Do wiadomości. */
  box?: "received" | "notifications";
  onClose: () => void;
  onDone?: () => void;
}

const TransferToAcceptanceModal = ({
  file,
  box = "received",
  onClose,
  onDone,
}: Props) => {
  const [workspaceId] = useState(getWorkspaceId() ?? "");
  const { data: workspaceResp } = workspaceService.useGetWorkspace(workspaceId);
  const workspace = workspaceResp?.data;
  const { mutate, isLoading } = acceptanceService.useTransfer();

  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [action, setAction] = useState<"accept" | "sign">("accept");
  const [note, setNote] = useState("");

  // Realni członkowie zespołu (z tożsamością i kluczem publicznym), bez siebie.
  const members = useMemo<IWorkspaceMember[]>(() => {
    const all = (workspace?.Members ?? []).filter((m) => !m.IsMe);
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (m) =>
        m.Label?.toLowerCase().includes(q) ||
        m.Email?.toLowerCase().includes(q),
    );
  }, [workspace, search]);

  const handleSubmit = () => {
    if (!selectedUserId) return;
    const payload: IAcceptanceTransferRequest = {
      RecipientUserId: selectedUserId,
      Box: box,
      Action: box === "received" ? action : undefined,
      DocumentType: file.documentType,
      DocumentId: file.documentId,
      FileUri: file.fileUri,
      EncryptionKeyId: file.encryptionKeyId,
      FileName: file.fileName,
      Title: file.title,
      Note: note.trim() || undefined,
    };
    mutate(payload, {
      onSuccess: () => {
        onDone?.();
        onClose();
      },
    });
  };

  const heading =
    box === "received" ? "Do akceptacji/podpisu" : "Do wiadomości";

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        // zatrzymaj propagację, by klik w oknie nie wyzwalał akcji rodzica
        // (np. podglądu pliku w menu załącznika)
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.panel}>
        <div className={styles.header}>
          <span className={styles.title}>{heading}</span>
          <button className={styles.closeBtn} onClick={onClose} title="Zamknij">
            ✕
          </button>
        </div>

        <div className={styles.fileRow}>
          <span className={styles.fileLabel}>Plik</span>
          <span className={styles.fileName}>
            {file.fileName || file.title || "—"}
          </span>
        </div>

        {box === "received" && (
          <div className={styles.actionToggle}>
            <button
              className={`${styles.toggleBtn} ${
                action === "accept" ? styles.toggleActive : ""
              }`}
              onClick={() => setAction("accept")}
            >
              Do akceptacji
            </button>
            <button
              className={`${styles.toggleBtn} ${
                action === "sign" ? styles.toggleActive : ""
              }`}
              onClick={() => setAction("sign")}
            >
              Do podpisu
            </button>
          </div>
        )}

        <TextField
          label="Szukaj osoby"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="standard"
          fullWidth
          size="small"
        />

        <div className={styles.memberList}>
          {members.length === 0 && (
            <div className={styles.empty}>Brak osób do wyświetlenia</div>
          )}
          {members.map((m) => (
            <div
              key={m.UserId || m.Id}
              className={`${styles.member} ${
                selectedUserId === m.UserId ? styles.memberSelected : ""
              }`}
              onClick={() => setSelectedUserId(m.UserId)}
            >
              <span className={styles.radio}>
                {selectedUserId === m.UserId ? "●" : "○"}
              </span>
              <span className={styles.memberName}>{m.Label || m.Email}</span>
              {!m.PublicKey && (
                <span
                  className={styles.noKey}
                  title="Brak klucza publicznego — odbiorca może nie odszyfrować pliku"
                >
                  brak klucza
                </span>
              )}
            </div>
          ))}
        </div>

        <TextField
          label="Notatka (opcjonalnie)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          variant="standard"
          fullWidth
          multiline
          maxRows={3}
        />

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>
            Anuluj
          </button>
          <button
            className={styles.submitBtn}
            onClick={handleSubmit}
            disabled={!selectedUserId || isLoading}
          >
            {isLoading ? "Przekazywanie…" : "Przekaż"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferToAcceptanceModal;
